import ko from "knockout";
import componentCache from "flow/utils/componentCache";
import ajaxErrorHelper from "flow/ajax/ajaxErrorHelper";
import config from "flow/config";
import MyFlow from "flow/MyFlow";
import { Dictionary } from "flow/api/types";
import Component from "flow/Component";
import { CanLeaveEntry } from "flow/Viewport";
import protect from "flow/protect";

type TabEntry = {
    id: string;
    title: KnockoutObservable<string>;
    isVisible: KnockoutObservable<boolean>;
    handle: KnockoutObservable<any>;
};

export class SideTabs {
    tabs = ko.observableArray<TabEntry>([]);
    isExpanded = ko.observable<boolean>(false);
    currentComponent = ko.observable<Component>(null);
    currentComponentId = ko.observable<string>(undefined);

    isLoading = ko.pureComputed<boolean>(() => {
        return this.currentComponent() !== null ? this.currentComponent().isLoading() : true;
    });

    init(myFlow: MyFlow, manifests: any[]): void {
        const sideTabArray = [];
        for (let i = 0; i < manifests.length; ++i) {
            sideTabArray.push({
                id: manifests[i].id,
                title: ko.observable(manifests[i].name),
                isVisible: ko.observable(false),
                handle: ko.observable(false)
            });

            this.tabs(sideTabArray);
        }

        myFlow.subscribe(config.topics.workingContextChangeStarted, () => {
            this.onHide();
        });

        this.isExpanded.subscribe(value => {
            myFlow.publish(config.topics.sideTabExpandedChanged, { isExpanded: value, id: this.currentComponentId() });
        });
    }

    loadComponentById(id: string, viewContext: Dictionary): void {
        const tab = this.tabs().find(t => {
            return t.id === id;
        });

        const focusDelay = tab.isVisible() ? 0 : 500; // Whether to wait for css animation delay or not.
        tab.isVisible(true);

        const component = componentCache.getComponentById(id);
        component
            .load()
            .done(() => {
                component.activate(viewContext);
                component.onShow();

                // Workaround needed since css animation will prevent focus from working correctly the 2nd time. :(
                setTimeout(() => {
                    component.onFocus();
                    tab.handle(true);
                }, focusDelay);
                if (viewContext && viewContext.autoOpen) this.sideTabClicked(tab);
            })
            .fail(possibleJqXhr => {
                tab.isVisible(false);
                if (ajaxErrorHelper.isjqXhrObject(possibleJqXhr)) {
                    if (ajaxErrorHelper.isConnectionProblem(possibleJqXhr)) {
                        component.myFlow.handleAjaxError(possibleJqXhr);
                    }
                }
            });
    }

    canLeave(): CanLeaveEntry[] {
        if (this.currentComponent() !== null) {
            const canLeaveResult = this.currentComponent().canLeave();

            if (canLeaveResult) return [{ component: this.currentComponent(), result: canLeaveResult }];
        }

        return [];
    }

    onHide(): void {
        if (this.currentComponent()) this.currentComponent().myFlow.clearLocalNotification();

        this.isExpanded(false);
        this.currentComponentId(undefined);
        this.currentComponent(null);

        this.tabs().forEach(t => {
            t.isVisible(false);
        });
    }

    sideTabClicked(tab: TabEntry): void {
        if (this.isExpanded()) {
            const currentComponent = this.currentComponent();
            const canLeaveMessage = this.currentComponent()?.canLeave();

            if (currentComponent && canLeaveMessage !== undefined) {
                const viewModel = currentComponent.viewModel();
                if (viewModel?.performCustomCanLeave) {
                    //passing on id and viewContext so that performCustomCanLeave can continue navigation if necessary
                    protect(currentComponent.myFlow, viewModel, viewModel.performCustomCanLeave)(
                        undefined,
                        undefined,
                        undefined
                    );
                    //exiting the activation process because conditional navigation continues through performCustomCanLeave
                    return;
                } else {
                    currentComponent.myFlow.notifyInfo(canLeaveMessage, currentComponent.manifest.name);
                    return;
                }
            }
            this.isExpanded(false);
            this.currentComponentId(undefined);
            this.currentComponent(null);
        } else {
            const component = componentCache.getComponentById(tab.id);
            this.currentComponentId(tab.id);
            this.currentComponent(component);

            if (!component.viewModel()) {
                component.load().done(() => {
                    component.onSelected();
                });
            } else {
                component.onSelected();
            }

            this.isExpanded(true);
        }
    }
}

const sideTabs = new SideTabs();
export default sideTabs;
